import { PurchaseIcon } from '@/src/assets/icons/PurchaseIcon'
import { formatDateToShortString } from '@/src/common/utils/date'
import { Detail } from '@/src/design-system/Detail'
import { ScrollView, View, Text } from 'react-native'
import { useLocalSearchParams } from 'expo-router'
import { Loading } from '@/src/common/components/Loading'
import { Fragment } from 'react'
import { startCase } from 'lodash'
import { formatCurrency } from '@/src/common/utils/general'
import { DesignSystem } from '@/src/design-system'
import { mesaColorNames, typography } from '@/src/common/theme'
import * as Components from '../components'
import { Button } from '@/src/common/components/Button'
import { CardActivityApi } from '../stores'

export function ActivityTransactionDetail() {
  const { id = '' } = useLocalSearchParams<{ id: string }>()

  const getCardTransaction = CardActivityApi.endpoints['GET /card/transactions/:id'].useQuery({
    params: {
      id,
    },
  })

  if (getCardTransaction.isUninitialized || getCardTransaction.isLoading) {
    return (
      <ScrollView contentContainerStyle={{ paddingBottom: 50 }}>
        <Loading />
      </ScrollView>
    )
  } else if (getCardTransaction.data) {
    const {
      createdAt,
      type,
      merchantName,
      merchantCategory,
      merchantDescription,
      merchantCity,
      merchantCountry,
      referenceNumber,
      pointsEarn,
      isoCurrencyCode,
      amountCents,
    } = getCardTransaction.data

    const postedDate = formatDateToShortString(new Date(createdAt))
    const totalPointsEarned = pointsEarn.reduce((total, pointsEarn) => total + pointsEarn.points, 0)
    const formattedPointsEarned = Number(totalPointsEarned).toLocaleString()

    const styledCardAdditionalStyles = {
      paddingLeft: 16,
      paddingRight: 16,
      paddingTop: 16,
      paddingBottom: 16,
      marginHorizontal: 16,
      display: 'flex',
      gap: 24,
    }

    const cardContainer = { display: 'flex', gap: 8 }

    return (
      <ScrollView contentContainerStyle={{ paddingBottom: 50, gap: 16 }}>
        <DesignSystem.Card style={styledCardAdditionalStyles}>
          <View style={cardContainer}>
            <DesignSystem.BoxedIcon Icon={PurchaseIcon} />
            <Text
              style={[
                { color: mesaColorNames['global/grey/70 #53575A'] },
                typography['paragraph/md'],
                !merchantName && { fontStyle: 'italic' },
              ]}
            >
              {merchantName}
            </Text>
            <Text style={typography['heading/heading3']}>
              {`${formatCurrency(`${amountCents / 100}`, {
                isFloat: true,
                minimumFractionDigits: 2,
                decimals: null,
              })} ${isoCurrencyCode}`}
            </Text>
            <Text style={{ color: mesaColorNames['Greyscale/500 #6E7375'], fontSize: 12 }}>
              {postedDate}
            </Text>
          </View>
        </DesignSystem.Card>

        {pointsEarn.length ? (
          <DesignSystem.Card style={styledCardAdditionalStyles}>
            <View style={cardContainer}>
              <Text style={typography['label/xs-bold']}>Reward details</Text>
              <Text
                style={[
                  typography['heading/heading3'],
                  { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
                ]}
              >
                {formattedPointsEarned}
              </Text>
              <Text
                style={{
                  ...typography['label/md-regular'],
                  color: mesaColorNames['global/grey/80 #2F3133'],
                }}
              >
                Mesa Points
              </Text>
              {pointsEarn.map((pointsEarn) => (
                <Fragment key={pointsEarn.reason}>
                  <View
                    style={{
                      backgroundColor: mesaColorNames['Greyscale/400 #A4AAAC'],
                      height: 1,
                      width: 16,
                    }}
                  />
                  <Text
                    style={{
                      ...typography['label/sm-regular'],
                      color: mesaColorNames['Greyscale/500 #6E7375'],
                    }}
                  >
                    {pointsEarn.reason}
                  </Text>
                </Fragment>
              ))}
            </View>
          </DesignSystem.Card>
        ) : null}

        <DesignSystem.Card style={styledCardAdditionalStyles}>
          <View style={cardContainer}>
            <Text style={typography['label/xs-bold']}>Transaction details</Text>
            <Detail category="Type" description={type === 'REFUND' ? 'Return' : 'Sale'} />
            <Detail category="Posted Date" description={postedDate} />
            <Detail category="Reference Number" description={referenceNumber} />
            <Detail
              category="Merchant Category"
              description={merchantCategory ? startCase(merchantCategory) : undefined}
            />
            <Detail
              category="Merchant Details"
              description={[merchantName, merchantCity, merchantCountry]
                .filter((value) => value)
                .join(', ')}
            />
            {merchantDescription && (
              <Detail category="Merchant Description" description={merchantDescription} />
            )}
          </View>
        </DesignSystem.Card>
        <Button title="Need help with this transaction?" onPress={() => null} />
      </ScrollView>
    )
  } else {
    return (
      <>
        <Components.Highnote.NoTransactionsIcon
          svgProps={{ style: { marginHorizontal: 'auto', marginTop: 32 } }}
          pathProps={{ fill: mesaColorNames['Mesa - Brand/White #FFFFFF'] }}
        />
        <Text
          style={[
            typography.L,
            {
              textAlign: 'center',
              color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
              marginTop: 32,
              marginBottom: 32,
            },
          ]}
        >
          Transaction not found
        </Text>
      </>
    )
  }
}
